import { Typography } from '@mui/material';
import React, { useContext, useMemo } from 'react';
import { Contact } from 'sr-types/lib/production/v1/graphql';
import { H } from '../../common/Layout';
import { useStyling } from '../../common/Theme';
import { UserContext } from '../../common/auth/UserContext';
import { GROUP_BY_FIELDS } from '../../common/components/forms/groupByConfigurations/groupByConstants';
import I18n, { useI18n } from '../../common/i18n/I18n';
import { aggregateItemsByNameAndRole } from '../../common/utils/commonUtils';
import useHistory from '../../common/utils/useHistory';
import RenderPeopleByName from '../../person/helpers/RenderPeopleByName';
import RenderPeopleByRole from '../../person/helpers/RenderPeopleByRole';
import { getFullName } from '../../person/person';
import { getWorkspaceDataCardPreference } from '../DataList/ProductionDataListConfiguration/productionDataListConfigurationUtils';

const projectOwnerContainerSx = { gap: 1, alignItems: 'center' };

export default function ProjectOwner({ projectOwners }: { projectOwners: Contact[] }) {
    const { theme } = useStyling();
    const { application, preferences } = useContext(UserContext);
    const { changeRoute } = useHistory();
    const projectOwnerLabelSx = useMemo(
        () => ({ color: theme.palette.text.secondary, flexShrink: 0, whiteSpace: 'nowrap' }),
        [theme.palette.text.secondary]
    );

    const { workspacePreferenceValue } = getWorkspaceDataCardPreference(preferences);

    const noRoleAssignedLabel = useI18n('production.header.keyPeople.no.role.assigned');

    const validOwners = useMemo(
        () => projectOwners?.filter((owner) => owner?.personReference?.label) || [],
        [projectOwners]
    );

    const getId = (owner: Contact) => owner.personReference?.id ?? '';
    const getName = (owner: Contact) => (typeof owner.name === 'string' ? owner.name : getFullName(owner.name));
    const getRoles = (owner: Contact) => owner.productionRoles ?? [];

    const groupByObject = useMemo(() => {
        return aggregateItemsByNameAndRole(
            validOwners,
            {
                getId,
                getName,
                getRoles
            },
            noRoleAssignedLabel
        );
    }, [noRoleAssignedLabel, validOwners]);

    const groupByPreference = workspacePreferenceValue.projectOwnersGroupBy ?? GROUP_BY_FIELDS.projectOwnersName;
    const shouldShowNameAsLabel = groupByPreference === GROUP_BY_FIELDS.projectOwnersName;

    const handleOwnerClick = (event: React.MouseEvent<HTMLButtonElement>, projectOwnerId?: string) => {
        event.stopPropagation();
        if (projectOwnerId) {
            changeRoute(`${application.home}/people/${projectOwnerId}/details`);
        }
    };

    return (
        <H sx={projectOwnerContainerSx}>
            <Typography className="ellipsis" sx={projectOwnerLabelSx}>
                <I18n token="production.header.projectOwner" />
            </Typography>
            {validOwners?.length > 0 ? (
                shouldShowNameAsLabel ? (
                    <RenderPeopleByName
                        peopleGroupedByName={groupByObject.byNameArray}
                        onPersonClick={handleOwnerClick}
                    />
                ) : (
                    <RenderPeopleByRole
                        peopleGroupedByRole={groupByObject.byRoleArray}
                        onPersonClick={handleOwnerClick}
                    />
                )
            ) : (
                <Typography className="ellipsis">
                    <I18n token="production.header.projectOwner.notSpecified" />
                </Typography>
            )}
        </H>
    );
}
